import Vue from 'vue';
import TYPES from '@/types';
import { currencyFormat } from '@/vue-app/utils/currency';

// Application
import GetRetirementInvestorGoalCalculatorQuery from '@/modules/flagship/retirement-investor-goal-calculator/application/queries/get-retirement-investor-goal-calculator-query';
import GetInvestorProfileQuery from '@/modules/flagship/investor-profile/investor-profile/application/queries/get-investor-profile-query';

// Domain
import { CreateInvestorGoalStateManager } from '@/modules/flagship/investor-goal/investor_goal/domain/state/create-investor-goal-state-manager';
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';
import { RetirementInvestorGoalCalculatorDto } from '@/modules/flagship/retirement-investor-goal-calculator/domain/dtos/retirement-investor-goal-calculator-dto';

type CardInfoItem = {
  label: string;
  value: string;
}

type CardInfo = {
  title: string;
  items: Array<CardInfoItem>;
  class: string;
  title_class: string;
  label_class: string;
  value_class: string;
  divider_color: string;
}

export default class RetirementFundContributionsViewModel {
  @Inject(TYPES.CREATE_INVESTOR_GOAL_STATE_MANAGER)
  readonly create_investor_goal_state_manager!: CreateInvestorGoalStateManager;

  @Inject(TYPES.GET_RETIREMENT_INVESTOR_GOAL_CALCULATOR_QUERY)
  private readonly get_retirement_investor_goal_calculator_query!:
    GetRetirementInvestorGoalCalculatorQuery;

  @Inject(TYPES.GET_INVESTOR_PROFILE_QUERY)
  private readonly get_investor_profile_query!: GetInvestorProfileQuery;

  @Inject(TYPES.I18N)
  private readonly translator!: Translator;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  readonly i18n_namespace = 'components.flagship.flagship-goals.create-goal.retirement-fund.inflation-info';

  readonly view: Vue;

  public constructor(view: Vue) {
    this.view = view;
  }

  footer_text = '';

  percentage_decrease = '0.00 %';

  is_loading = false;

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  cards: Array<CardInfo> = []

  calculateSurplusAmount = async () => {
    const investor_profile = await this.get_investor_profile_query.execute();
    let surplus_amount = investor_profile.net_monthly_income - investor_profile
      .net_monthly_expenses;
    if (surplus_amount <= 0) {
      surplus_amount = investor_profile.net_monthly_income * 0.10;
    }
    return surplus_amount;
  }

  calculateRetirementPlan = async (
    retirement_goal_calculator_dto: RetirementInvestorGoalCalculatorDto,
  ) => {
    try {
      const { retirement_calculation_data } = await this
        .get_retirement_investor_goal_calculator_query.execute({
          ...retirement_goal_calculator_dto,
        });

      return retirement_calculation_data;
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.load_monthly_required_amount'));
      return {
        initial_amount: 0,
        interest_rate: 0,
        monthly_required_amount_with_inflation: 0,
        monthly_required_amount_without_inflation: 0,
        retirement_range_adjusted: 0,
        accumulated_amount_with_inflation: 0,
        accumulated_amount_without_inflation: 0,
        monthly_pension_with_inflation: 0,
        percentage_decrease: 0,
        pension_desired_in_future_with_inflation: 0,
        pension_desired_in_future_without_inflation: 0,
        monthly_pension_without_inflation: 0,
        pension_range_adjusted: 0,
      };
    }
  }

  loadInformation = async (retirement_goal_calculator_dto: RetirementInvestorGoalCalculatorDto) => {
    if (retirement_goal_calculator_dto.monthly_pension_adjusted === 0
      && retirement_goal_calculator_dto.monthly_required_amount === 0) {
      // eslint-disable-next-line no-param-reassign
      retirement_goal_calculator_dto.monthly_required_amount = await this.calculateSurplusAmount();
      // eslint-disable-next-line no-param-reassign
      retirement_goal_calculator_dto.defined_by = 'contribution';
    }
    const retirement_plan = await this.calculateRetirementPlan(
      retirement_goal_calculator_dto,
    );
    const retirement_range = retirement_goal_calculator_dto.retirement_age_adjusted
      - retirement_goal_calculator_dto.issued_age;
    this.cards.push({
      title: this.translate('without.title'),
      items: [
        {
          label: this.translate('without.desired_pension'),
          value: `${currencyFormat(retirement_plan.monthly_pension_without_inflation)} MXN`,
        },
        {
          label: this.translate('without.target_amount'),
          value: `${currencyFormat(retirement_plan.accumulated_amount_without_inflation)} MXN`,
        },
        {
          label: this.translate('monthly_contribution'),
          value: `${currencyFormat(retirement_plan.monthly_required_amount_without_inflation)} MXN`,
        },
      ],
      class: 'white',
      title_class: 'primary--text',
      label_class: 'primary--text',
      value_class: 'sky-blue--text',
      divider_color: '',
    }, {
      title: this.translate('with.title'),
      items: [
        {
          label: this.translate('with.desired_pension'),
          value: `${currencyFormat(retirement_plan.monthly_pension_with_inflation)} MXN`,
        },
        {
          label: this.translate('with.target_amount'),
          value: `${currencyFormat(retirement_plan.accumulated_amount_with_inflation)} MXN`,
        },
        {
          label: this.translate('monthly_contribution'),
          value: `${currencyFormat(retirement_plan.monthly_required_amount_with_inflation)} MXN`,
        },
      ],
      class: 'sky-blue',
      title_class: 'white--text',
      label_class: 'white--text',
      value_class: 'white--text',
      divider_color: 'white',
    });
    this.footer_text = this.translate('description_1',
      {
        desired_pension: `${currencyFormat(retirement_plan.monthly_pension_without_inflation)} MXN`,
        retirement_range,
        desired_pension_with_inflation: `${currencyFormat(
          retirement_plan.pension_desired_in_future_with_inflation,
        )} MXN`,
      });
    this.percentage_decrease = `${retirement_plan.percentage_decrease} %`;
  }

  initialize = async (retirement_goal_calculator_dto: RetirementInvestorGoalCalculatorDto) => {
    this.is_loading = true;
    await this.loadInformation(retirement_goal_calculator_dto);
    this.is_loading = false;
  }
}
